/**
 * /settings/api-keys — one simple place to paste, save, and test every API key.
 *
 * Driven entirely off GET /api/admin/secrets (the backend registry), so adding a new
 * integration on the server automatically shows up here. Values are never read
 * back (the API only returns status + last-4); you paste a new value to set or
 * rotate. "Test" calls the provider live where supported.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/settings/api-keys")({
  component: ApiKeysPage,
});

interface SecretStatus {
  key_name: string;
  label: string;
  scope: "platform" | "tenant";
  description: string;
  placeholder?: string | null;
  docs_url?: string | null;
  is_set: boolean;
  source: "database" | "env" | "unset";
  last_4?: string | null;
}
interface TestResult { ok: boolean; detail?: string; error?: string }

// Group keys into friendly sections. Anything unmapped falls into "Other".
const GROUPS: { title: string; keys: string[] }[] = [
  { title: "AI", keys: ["anthropic_api_key", "openai_api_key", "google_api_key", "mistral_api_key", "ollama_base_url"] },
  { title: "Billing (Stripe)", keys: ["stripe_secret_key", "stripe_webhook_secret"] },
  { title: "QuickBooks (Intuit)", keys: ["intuit_client_id", "intuit_client_secret"] },
  { title: "Email (SMTP)", keys: ["smtp_host", "smtp_port", "smtp_username", "smtp_password", "smtp_from_address"] },
  { title: "Avatar", keys: ["anam_api_key"] },
  { title: "Tax & payroll filing", keys: ["taxjar_api_key", "symmetry_api_key", "iris_client_id", "iris_client_secret", "iris_environment", "tax1099_api_key", "tax1099_environment", "efile_provider"] },
  { title: "Edge / Workspace", keys: ["bynaus_edge_url", "bynaus_workspace_url", "ai_route_through_edge"] },
];

function ApiKeysPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const { data: secrets, isLoading } = useQuery({
    queryKey: ["secrets"],
    queryFn: () => api<SecretStatus[]>("/api/admin/secrets"),
  });

  const byName = useMemo(() => {
    const m: Record<string, SecretStatus> = {};
    (secrets ?? []).forEach((s) => { m[s.key_name] = s; });
    return m;
  }, [secrets]);

  // Build grouped view; collect any registry keys not in GROUPS into "Other".
  const grouped = useMemo(() => {
    const known = new Set(GROUPS.flatMap((g) => g.keys));
    const other = (secrets ?? []).filter((s) => !known.has(s.key_name)).map((s) => s.key_name);
    const all = other.length ? [...GROUPS, { title: "Other", keys: other }] : GROUPS;
    const q = search.trim().toLowerCase();
    return all
      .map((g) => ({
        title: g.title,
        rows: g.keys.map((k) => byName[k]).filter(Boolean).filter((s) =>
          !q || s.label.toLowerCase().includes(q) || s.key_name.includes(q)),
      }))
      .filter((g) => g.rows.length > 0);
  }, [secrets, byName, search]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["secrets"] });

  // Collapsible sections — collapsed by default so the page stays short.
  // A search forces every matching section open so results are always visible.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const searching = search.trim().length > 0;
  const isOpen = (title: string) => searching || !!expanded[title];
  const toggle = (title: string) =>
    setExpanded((e) => ({ ...e, [title]: !e[title] }));

  // Split into the two areas. A group is platform/tenant based on its keys'
  // scope. The platform area only appears for Bynaus platform admins (the
  // backend already filters platform keys out for everyone else).
  const platformGroups = grouped.filter((g) => g.rows[0]?.scope === "platform");
  const tenantGroups = grouped.filter((g) => g.rows[0]?.scope === "tenant");

  const setAll = (open: boolean) =>
    setExpanded(Object.fromEntries(grouped.map((g) => [g.title, open])));

  const renderGroups = (groups: typeof grouped) =>
    groups.map((g) => {
      const open = isOpen(g.title);
      const setCount = g.rows.filter((r) => r.is_set).length;
      return (
        <section key={g.title} className="mb-2">
          <button
            type="button" onClick={() => toggle(g.title)}
            className="w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-gray-800/60"
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="text-slate-400 text-xs w-3">{open ? "▾" : "▸"}</span>
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{g.title}</span>
              <span className="text-xs text-slate-400">({g.rows.length})</span>
            </span>
            <span className={`shrink-0 text-[11px] rounded-full px-2 py-0.5 ${setCount > 0 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-slate-100 text-slate-400 dark:bg-gray-800"}`}>
              {setCount}/{g.rows.length} set
            </span>
          </button>
          {open && (
            <div className="mt-1.5 rounded-xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 divide-y divide-slate-100 dark:divide-gray-800">
              {g.rows.map((s) => <SecretRow key={s.key_name} secret={s} onChanged={refresh} />)}
            </div>
          )}
        </section>
      );
    });

  return (
    <div className="h-full overflow-y-auto bg-slate-50 dark:bg-gray-950 px-6 py-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">API keys</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Paste a key to set or rotate it, then Test. Stored securely server-side and never shown back —
            you only see whether one is set and its last 4 characters.
          </p>
        </div>

        {/* Prominent search bar + expand/collapse all */}
        <div className="mb-4 flex items-center gap-2">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">🔍</span>
            <input
              type="search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search API keys by name…"
              className="w-full rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {!searching && (
            <button
              type="button"
              onClick={() => setAll(!grouped.every((g) => expanded[g.title]))}
              className="shrink-0 rounded-lg border border-slate-200 dark:border-gray-700 px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800"
            >{grouped.every((g) => expanded[g.title]) ? "Collapse all" : "Expand all"}</button>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <>
            {tenantGroups.length > 0 && (
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">Your integrations</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  Keys for your own service accounts — enter your own.
                </p>
                {renderGroups(tenantGroups)}
              </div>
            )}
            {platformGroups.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Bynaus platform keys</h2>
                  <span className="rounded-full bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Admin</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  Bynaus-managed vendor-app credentials (AI, the QuickBooks &amp; Plaid apps, platform billing, email).
                  Visible because you're a platform admin; ordinary customer admins never see these.
                </p>
                {renderGroups(platformGroups)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SecretRow({ secret, onChanged }: { secret: SecretStatus; onChanged: () => void }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState<null | "save" | "test">(null);
  const [test, setTest] = useState<TestResult | null>(null);

  const save = async () => {
    if (!value.trim()) return;
    setBusy("save"); setTest(null);
    try {
      await api(`/api/admin/secrets/${secret.key_name}`, { method: "PUT", body: { value: value.trim() } });
      setValue("");
      onChanged();
    } catch (e) {
      setTest({ ok: false, error: (e as Error).message });
    } finally { setBusy(null); }
  };

  const runTest = async () => {
    setBusy("test"); setTest(null);
    try {
      const r = await api<TestResult>(`/api/admin/secrets/${secret.key_name}/test`, { method: "POST", body: {} });
      setTest(r);
    } catch (e) {
      setTest({ ok: false, error: (e as Error).message });
    } finally { setBusy(null); }
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{secret.label}</span>
            {secret.is_set ? (
              <span className="rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 px-2 py-0.5 text-[11px] font-medium">
                Set{secret.last_4 ? ` · ···${secret.last_4}` : ""}{secret.source === "env" ? " · env" : ""}
              </span>
            ) : (
              <span className="rounded-full bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-slate-400 px-2 py-0.5 text-[11px]">Not set</span>
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{secret.description}</p>
        </div>
        {secret.docs_url && (
          <a href={secret.docs_url} target="_blank" rel="noreferrer" className="shrink-0 text-xs text-blue-600 hover:underline">Get key ↗</a>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="password" autoComplete="off" value={value} onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
          placeholder={secret.is_set ? "Paste a new value to rotate…" : (secret.placeholder ?? "Paste value…")}
          className="flex-1 rounded-md border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button" onClick={save} disabled={busy !== null || !value.trim()}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
        >{busy === "save" ? "Saving…" : "Save"}</button>
        {secret.is_set && (
          <button
            type="button" onClick={runTest} disabled={busy !== null}
            className="rounded-md border border-slate-200 dark:border-gray-700 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800 disabled:opacity-40"
          >{busy === "test" ? "Testing…" : "Test"}</button>
        )}
      </div>
      {test && (
        <p className={`mt-1.5 text-xs ${test.ok ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
          {test.ok ? "✓ " : "✗ "}{test.detail || test.error || (test.ok ? "OK" : "Failed")}
        </p>
      )}
    </div>
  );
}
